import { ApiService, Authentication } from '../../common'
import { Account, Pagination } from '@one/types'
import { AxiosPromise } from 'axios'

export class AccountAppApi extends ApiService {
  createUser(dataUser: Account.Requests.CreateUserOption): AxiosPromise<void> {
    this.invalidate(['users'])
    return this.post(`/users`, dataUser, {
      authentication: Authentication.REQUIRED
    })
  }

  updateUser(
    idUser: string,
    dataUser: Account.Requests.EditUserOption
  ): AxiosPromise<void> {
    this.invalidate(['users'])
    return this.put(`/users/${idUser}`, dataUser, {
      authentication: Authentication.REQUIRED
    })
  }

  deleteUser(idUser: string): AxiosPromise<void> {
    this.invalidate(['users'])
    return this.delete(`/users/${idUser}`, {
      authentication: Authentication.REQUIRED
    })
  }

  getCurrentUser(): AxiosPromise<Account.Responses.User> {
    return this.get('/users/self', {
      authentication: Authentication.REQUIRED
    })
  }

  getUserBasedOnToken(token: string): AxiosPromise<Account.Responses.User> {
    return this.get('/users/self', {
      authentication: Authentication.REQUIRED,
      injectToken: false,
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
  }

  updateCurrentUser(
    dataUser: Account.Requests.EditUserOption
  ): AxiosPromise<void> {
    return this.put(`/users/self`, dataUser, {
      authentication: Authentication.REQUIRED
    })
  }

  updateCurrentPassword(
    dataUser: Account.Requests.Password
  ): AxiosPromise<void> {
    return this.put(`/users/self/password`, dataUser, {
      authentication: Authentication.REQUIRED
    })
  }

  updateCurrentPasswordWithoutToken(
    dataUser: Account.Requests.Password,
    token?: string
  ): AxiosPromise<void> {
    return this.put(`/users/self/password`, dataUser, {
      authentication: Authentication.REQUIRED,
      injectToken: false,
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
  }

  editIsActiveUser(
    idUser: string,
    dataIsActive: Account.Requests.SetIsActiveOption
  ): AxiosPromise<void> {
    this.invalidate(['users'])
    return this.put(`/users/${idUser}/active`, dataIsActive, {
      authentication: Authentication.REQUIRED
    })
  }

  getUsersList(
    data: Account.Requests.UsersListPager
  ): AxiosPromise<Pagination.Pager<Account.Responses.User>> {
    return this.get(
      `/users/my-organization-users?pageSize=${data.pageSize}&pageNumber=${data.pageNumber}`,
      {
        authentication: Authentication.REQUIRED,
        cache: {
          tags: ['users']
        }
      }
    )
  }

  getAccountRegistrationSetting(): AxiosPromise<
    Account.Responses.RegistrationSettings
  > {
    return this.get('/settings/registration', {
      authentication: Authentication.PUBLIC
    })
  }

  getAccountWarehouseSetting(): AxiosPromise<
    Account.Responses.WarehouseSettingsDto
  > {
    return this.get('/settings/warehouse', {
      authentication: Authentication.PUBLIC,
      cache: {
        ttl: 60 * 100,
        tags: ['warehouse-setting']
      }
    })
  }

  getAgreementsForCurrentUser(): AxiosPromise<
    Array<Account.Responses.Agreement>
  > {
    return this.get('/agreements', {
      authentication: Authentication.REQUIRED,
      cache: {
        ttl: 60 * 100,
        tags: ['agreements']
      }
    })
  }

  getElectronicInvoicesAgreementForCurrentUser(): AxiosPromise<
    Account.Responses.InvoiceAgreement
  > {
    return this.get('/agreements/electronic_invoices', {
      authentication: Authentication.REQUIRED
    })
  }

  getListAgreements(
    clientType: string
  ): AxiosPromise<Array<Account.Responses.Agreement>> {
    return this.get('/agreements', {
      params: {
        clientType
      },
      authentication: Authentication.PUBLIC
    })
  }

  getElectronicInvoicesAgreement(): AxiosPromise<
    Account.Responses.InvoiceAgreement
  > {
    return this.get('/agreements/electronic_invoices', {
      authentication: Authentication.PUBLIC
    })
  }

  updateAgreement(
    agreementId: string,
    data: Account.Requests.EditAgreement
  ): AxiosPromise<void> {
    this.invalidate(['agreements'])
    return this.put(`/agreements/${agreementId}`, data, {
      authentication: Authentication.REQUIRED
    })
  }

  updateAgreementWithoutToken(
    agreementId: string,
    data: Account.Requests.EditAgreement,
    token?: string
  ): AxiosPromise<void> {
    this.invalidate(['agreements'])
    return this.put(`/agreements/${agreementId}`, data, {
      authentication: Authentication.REQUIRED,
      injectToken: false,
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
  }

  updateElectronicInvoicesAgreement(
    data: Account.Requests.EditInvoiceAgreement
  ): AxiosPromise<void> {
    return this.put('/agreements/electronic_invoices', data, {
      authentication: Authentication.REQUIRED
    })
  }

  getClientAddresses(
    data: Account.Requests.AddressesPager
  ): AxiosPromise<Pagination.Pager<Account.Responses.Address>> {
    return this.get(
      `/addresses/my-organization-addresses?rows=${data.rows}&start=${data.start}`,
      {
        authentication: Authentication.REQUIRED,
        cache: {
          tags: ['client', 'address']
        }
      }
    )
  }

  createAddress(address: Account.Requests.Address): AxiosPromise<string> {
    this.invalidate(['address'])
    return this.post(`/addresses`, address, {
      authentication: Authentication.REQUIRED
    })
  }

  deleteAddress(id: string): AxiosPromise<void> {
    this.invalidate(['address'])
    return this.delete(`/addresses/${id}`, {
      authentication: Authentication.REQUIRED
    })
  }

  setDefaultAddress(id: string): AxiosPromise<void> {
    this.invalidate(['address'])
    return this.post(
      `/addresses/${id}/default-shipping`,
      {},
      {
        authentication: Authentication.REQUIRED
      }
    )
  }

  getClient(): AxiosPromise<Account.Responses.ClientRestView> {
    return this.get('/clients/self', {
      authentication: Authentication.REQUIRED,
      cache: {
        tags: ['client']
      }
    })
  }

  createOrganization(
    data: Account.Requests.CreateOrganization
  ): AxiosPromise<void> {
    return this.post('/organizations', data, {
      authentication: Authentication.PUBLIC
    })
  }

  updateOrganization(
    addressId: string | undefined,
    data: Account.Requests.EditOrganization
  ): AxiosPromise<void> {
    this.invalidate(['client'])
    return this.put('/organizations/self', data, {
      params: {
        addressId
      },
      authentication: Authentication.REQUIRED
    })
  }

  createIndividualUser(
    data: Account.Requests.CreateIndividualClient
  ): AxiosPromise<void> {
    return this.post('/individual-clients', data, {
      authentication: Authentication.PUBLIC
    })
  }

  updateIndividualUser(
    addressId: string,
    data: Account.Requests.EditIndividualClient
  ): AxiosPromise<void> {
    this.invalidate(['client'])
    return this.put('/individual-clients/self', data, {
      params: {
        addressId
      },
      authentication: Authentication.REQUIRED
    })
  }

  getUsersWithPickupPermission(): AxiosPromise<
    Array<Account.Responses.UserQueryDTO>
  > {
    return this.get('/users/authorized-to-receive-delivery', {
      authentication: Authentication.REQUIRED
    })
  }

  getUsersWithAuthorizationPermission(): AxiosPromise<
    Array<Account.Responses.UserQueryDTO>
  > {
    return this.get('/users/authorized-to-authorize-carts', {
      authentication: Authentication.REQUIRED
    })
  }

  getTradeCreditByClient(): AxiosPromise<Account.Responses.TradeCredit> {
    return this.get(`/trade-credits/my-organization-trade-credit`, {
      authentication: Authentication.REQUIRED
    })
  }

  getTradeCreditForPayer(): AxiosPromise<Account.Responses.TradeCredit> {
    return this.get('/trade-credits/my-payer-trade-credit', {
      authentication: Authentication.REQUIRED
    })
  }

  getTaxpayerAddress(
    tin: string
  ): AxiosPromise<Account.Responses.BillingAddress> {
    return this.get(`/taxpayer/address?tin=${tin}`, {
      authentication: Authentication.PUBLIC
    })
  }

  getAdditionalFieldsVisible(
    viewName: string
  ): AxiosPromise<Account.Responses.AdditionalFields> {
    return this.get(
      `/configurations/additional-fields/visible?viewName=${viewName}`,
      {
        authentication: Authentication.PUBLIC
      }
    )
  }
}
